import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Grid, Box, Button,
  useMediaQuery
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import { useTheme } from '@mui/material/styles';

export default function InvoiceReportDataGrid({ invoices, summary }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  // ✅ CSV Export Handler
  const handleDownloadCSV = () => {
    if (!invoices || invoices.length === 0) return;

    const headers = [
      "Invoice Number",
      "Status",
      "Date",
      "Due Date",
      "Pet Name",
      "Owner",
      "Total Amount",
      "Total Paid",
      "Remaining Balance",
      "Doctor",
      "Procedure"
    ];

    const rows = invoices.map((row) => [
      row.invoice_number,
      row.invoice_status,
      formatDate(row.invoice_date),
      formatDate(row.invoice_due_date),
      row.pet_name,
      row.pet_owner_name,
      row.total_amount,
      row.total_paid,
      row.remaining_balance,
      row.attending_doctor || "-",
      row.procedure || "-"
    ]);

    const csvContent =
      [headers, ...rows]
        .map((e) => e.join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "invoices_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      {/* ✅ Summary Scorecards */}
      {summary && (
        <Grid container spacing={1} sx={{ mb: 3 }} >
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 1, textAlign: 'left', border: '1px solid lightgray' }}>
              <Typography variant="caption">Total</Typography>
              <Typography variant="body1" color="primary">
                {numberToCurrencyFormat(summary.totalAmount.toFixed(2))}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 1, textAlign: 'left',border: '1px solid lightgray' }}>
              <Typography variant="caption">Paid</Typography>
              <Typography variant="body1" color="success.main">
                {numberToCurrencyFormat(summary.totalPaid.toFixed(2))}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 1, textAlign: 'left', border: '1px solid lightgray' }}>
              <Typography variant="caption">Outstanding</Typography>
              <Typography variant="body1" color="error.main">
                {numberToCurrencyFormat(summary.totalOutstanding.toFixed(2))}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ✅ Download Button */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" fullWidth={!isDesktop} color="primary" onClick={handleDownloadCSV}>
          <DownloadIcon /> Download CSV
        </Button>
      </Box>

      {/* ✅ Invoice Table with Sticky Header */}
      <TableContainer component={Paper} sx={{ maxHeight: 500, border: '1px solid lightgray' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {[
                'Invoice Number',
                'Status',
                'Date',
                'Due Date',
                'Pet Name',
                'Owner',
                'Total Amount',
                'Total Paid',
                'Remaining Balance',
                'Doctor',
                'Procedure',
              ].map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    verticalAlign: 'top',
                    lineHeight: 1,
                    py: 2,
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                  align={['Total Amount','Total Paid','Remaining Balance'].includes(header) ? 'right' : 'center'}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.invoice_number}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.invoice_status}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5, whiteSpace: 'nowrap' }}  align='center'>
                  {formatDate(row.invoice_date)}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5, whiteSpace: 'nowrap' }}  align='center'>
                  {formatDate(row.invoice_due_date)}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.pet_name}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.pet_owner_name}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align="right">
                  {numberToCurrencyFormat(row.total_amount)}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align="right">
                  {numberToCurrencyFormat(row.total_paid)}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align="right">
                  {numberToCurrencyFormat(row.remaining_balance)}
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.attending_doctor || '-'}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.procedure || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
